import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import useBaseService from "../../../../hooks/services/useBaseService";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import useForm from "../../../../hooks/useForm";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import SectionHeader from "../../../../components/forms/SectionHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Form from "../../../../components/forms/Form";
import MainLayout from "../../../../layouts/MainLayout";
import { typeFields } from "../../../../constants/forms/almoxarifado/typeFields";
import useUrlFilters from "../../../../hooks/useUrlFilters";
import { cleanPayload } from "../../../../utils/objectUtils";
import useSelectOptions from "../../../../hooks/useSelectOptions";

const CreateTypePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { filters, pagination } = useUrlFilters(
    {
      id: [],
      name: [],
      deleted_at: false,
    },
    "almox_types",
  );

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const {
    post: postRequest,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { formData, handleChange, resetForm, setFormData } = useForm(
    setDefaultFieldValues(typeFields),
  );

  const isKitOptions = useMemo(
    () => [
      { value: true, label: "Sim" },
      { value: false, label: "Não" },
    ],
    [],
  );

  const isKitSelect = useSelectOptions({
    fieldId: "type.is_kit",
    formData,
    rawOptions: isKitOptions,
    isMulti: false,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selects = {
    "type.is_kit": isKitSelect,
  };

  useEffect(() => {
    setFormData((prev) => ({
      type: {
        ...prev.type,
        is_kit: { value: true, label: "Sim" },
      },
    }));
  }, [setFormData]);

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  // --- Funções para Selects (getOptions e getSelectedValue) ---
  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.options || [];
    },
    [selects],
  );

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [selects],
  );

  // --- Mutação para criar um novo Tipo ---
  const createTypeMutation = useMutation({
    mutationFn: async (dataToCreate) => {
      showLoader();
      setFormErrors({});
      const response = await postRequest(entities.types.create, dataToCreate);
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        hideLoader();
        resetForm();
        queryClient.invalidateQueries({
          queryKey: ["almox_types", filters, pagination],
        });
        navigate(`/almoxarifado/produtos/tipos`, {
          state: { type: "success" },
        });
      }
    },
    onError: (error) => {
      console.log("onError ", error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedType = cleanPayload(formData);
      const payload = {
        type: {
          ...cleanedType.type,
        },
      };
      await createTypeMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao enviar tipo:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, createTypeMutation]);

  const handleBack = useCallback(() => {
    navigate("/almoxarifado/produtos/tipos");
  }, [navigate]);

  // useEffect para mensagens de estado de localização (mantido)
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const renderForm = () => {
    const renderedSections = new Set();

    return typeFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
            getOptions={getOptions}
            getSelectedValue={getSelectedValue}
            getSelectMeta={getSelectMeta}
          />
        </div>
      );
    });
  };

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastro de Tipos"
        showBackButton={true}
        backUrl="/almoxarifado/produtos/tipos"
      />

      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Cadastrar"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}
          isLoading={createTypeMutation.isPending}>
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateTypePage;
